/* ==========================================================================
   Mirah — theme.js (core)
   Smooth scroll, motion utilities, preloader, page transitions, header,
   mobile menu, accordions, scrollbar and shared helpers.
   Timings mirror the reference: base 1.2s "customSlow", reveals 1.4s expo.out,
   stagger .15, ScrollTrigger start "top bottom-=20%" (mobile "top bottom").
   ========================================================================== */
(function () {
  'use strict';

  var d = document;
  var html = d.documentElement;
  var settings = window.MirahSettings || {};
  var mqMobile = window.matchMedia('(max-width: 767.98px)');
  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var design = !!(window.Shopify && window.Shopify.designMode);
  var motionOn = html.classList.contains('js-motion') && !!window.gsap;

  var Theme = (window.Theme = window.Theme || {});
  Theme.booted = true;
  Theme.design = design;
  Theme.motionOn = motionOn;
  Theme.isMobile = function () { return mqMobile.matches; };

  /* ---------- Helpers ---------- */
  var $$ = function (root, sel) { return Array.prototype.slice.call((root || d).querySelectorAll(sel)); };
  Theme.$$ = $$;
  Theme.debounce = function (fn, wait) {
    var t;
    return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, wait); };
  };
  Theme.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = format || settings.moneyFormat || '{{amount}}';
    var value = '';
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var fmt = function (n, precision, thousands, decimal) {
      n = (Number(n) / 100).toFixed(precision);
      var parts = n.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
    };
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = fmt(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      default: value = fmt(cents, 2, ',', '.');
    }
    value = value.replace(/[.,]00$/, ''); // match Liquid's money_without_trailing_zeros
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  };
  Theme.fetchSection = function (url, sectionId) {
    var u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    return fetch(u.toString(), { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (text) { return new DOMParser().parseFromString(text, 'text/html'); });
  };

  /* ---------- Scroll lock (ref-counted) ---------- */
  var locks = 0;
  Theme.lockScroll = function () {
    locks++;
    if (Theme.lenis) Theme.lenis.stop();
    html.style.overflow = 'hidden';
  };
  Theme.unlockScroll = function () {
    locks = Math.max(0, locks - 1);
    if (locks) return;
    if (Theme.lenis && readyDone) Theme.lenis.start();
    html.style.removeProperty('overflow');
  };

  /* ---------- Focus trap ---------- */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  var trap = null;
  Theme.trapFocus = function (container, initial) {
    Theme.releaseFocus();
    var last = d.activeElement;
    var handler = function (e) {
      if (e.key !== 'Tab') return;
      var items = $$(container, FOCUSABLE).filter(function (el) { return el.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0], end = items[items.length - 1];
      if (e.shiftKey && d.activeElement === first) { e.preventDefault(); end.focus(); }
      else if (!e.shiftKey && d.activeElement === end) { e.preventDefault(); first.focus(); }
    };
    d.addEventListener('keydown', handler);
    trap = { handler: handler, last: last };
    requestAnimationFrame(function () { (initial || $$(container, FOCUSABLE)[0] || container).focus({ preventScroll: true }); });
  };
  Theme.releaseFocus = function (restore) {
    if (!trap) return;
    d.removeEventListener('keydown', trap.handler);
    if (restore !== false && trap.last && trap.last.focus) trap.last.focus({ preventScroll: true });
    trap = null;
  };

  /* ---------- Ready gate (preloader / page transition) ---------- */
  var readyDone = false;
  var readyResolve;
  Theme.ready = new Promise(function (r) { readyResolve = r; });
  Theme.ready.then(function () { readyDone = true; html.classList.add('is-ready'); if (Theme.lenis && !locks) Theme.lenis.start(); });

  /* ---------- Viewport unit for mobile browser bars ---------- */
  var setVh = function () { html.style.setProperty('--vh', window.innerHeight * 0.01 + 'px'); };
  setVh();

  /* ==========================================================================
     GSAP setup
     ========================================================================== */
  if (motionOn) {
    gsap.registerPlugin(ScrollTrigger, SplitText, CustomEase, DrawSVGPlugin);
    CustomEase.create('customExpo', 'M0,0 C0.124,0.012 0.154,0.016 0.186,0.046 0.266,0.121 0.317,0.288 0.349,0.498 0.383,0.726 0.437,0.875 0.491,0.924 0.493,0.926 0.519,0.94 0.52,0.941 0.523,0.942 0.527,0.944 0.531,0.945 0.605,0.973 0.76,1 1,1');
    CustomEase.create('customSlow', 'M0,0 C0.17,0.84 0.44,1 1,1');
    gsap.defaults({ ease: 'customSlow', duration: 1.2 });
    gsap.config({ nullTargetWarn: false });
    // Mobile starts a little inside the fold so reveals play where they can be seen, not below the browser bar.
    ScrollTrigger.defaults({ start: Theme.isMobile() ? 'top bottom-=10%' : 'top bottom-=20%' });
    ScrollTrigger.config({ ignoreMobileResize: true });

    if (settings.smoothScroll && !design && window.Lenis) {
      // A lerp glide (rather than a fixed duration) so long wheel runs keep flowing and settle softly.
      Theme.lenis = new Lenis({
        lerp: 0.075,
        wheelMultiplier: 0.95,
        orientation: 'vertical',
        gestureOrientation: 'vertical'
      });
      Theme.lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function (time) { Theme.lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
      Theme.lenis.stop();
    }
  } else {
    html.classList.remove('js-motion');
    html.classList.add('no-motion');
  }

  Theme.scrollTo = function (target, opts) {
    opts = opts || {};
    if (Theme.lenis) return Theme.lenis.scrollTo(target, Object.assign({ duration: 1, force: true }, opts));
    var y = typeof target === 'number' ? target : target.getBoundingClientRect().top + window.scrollY + (opts.offset || 0);
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  /* ==========================================================================
     Motion utilities — opt in with data attributes
     ========================================================================== */
  var ctxs = new WeakMap();
  var afterReady = function (tl) { Theme.ready.then(function () { tl.play(); }); return tl; };

  function splitReveal(root) {
    $$(root, '[data-split]').forEach(function (el) {
      if (el._split) return;
      var words = el.hasAttribute('data-split-words');
      var onLoad = el.hasAttribute('data-reveal-load');
      var vars = {
        yPercent: parseFloat(el.getAttribute('data-split-y')) || 105,
        stagger: 0.15,
        delay: parseFloat(el.getAttribute('data-reveal-delay')) || 0,
        duration: 1.4,
        ease: 'expo.out'
      };
      // aria-label is only valid on elements with a naming role (headings); elsewhere keep the DOM text readable.
      var isHeading = /^H[1-6]$/.test(el.tagName);
      el._split = SplitText.create(el, {
        type: words ? 'lines,words' : 'lines',
        mask: 'lines',
        aria: isHeading ? 'auto' : 'none',
        linesClass: 'line',
        wordsClass: 'word',
        autoSplit: true,
        onSplit: function (self) {
          el.classList.add('is-split');
          var targets = words ? self.words : self.lines;
          if (onLoad) return afterReady(gsap.from(targets, Object.assign({ paused: true }, vars)));
          return gsap.from(targets, Object.assign({ scrollTrigger: { trigger: el } }, vars));
        }
      });
    });
  }

  function fades(root) {
    $$(root, '[data-fade]').forEach(function (el) {
      gsap.fromTo(el, { opacity: 0 }, { opacity: 1, scrollTrigger: { trigger: el } });
    });
    $$(root, '[data-fade-bottom]').forEach(function (el) {
      gsap.fromTo(el, { y: 40, opacity: 0 }, { y: 0, opacity: 1, delay: parseFloat(el.getAttribute('data-reveal-delay')) || 0, scrollTrigger: { trigger: el } });
    });
    $$(root, '[data-stagger]').forEach(function (group) {
      var items = $$(group, '[data-stagger-item]');
      if (!items.length) return;
      gsap.fromTo(items, { y: 40, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.1, scrollTrigger: { trigger: group } });
    });
    // Pop: scale up out of a fade (scale only, so it composes with a drift's y), the icon spinning in a beat later.
    $$(root, '[data-pop]').forEach(function (el) {
      var tl = gsap.timeline({ delay: parseFloat(el.getAttribute('data-reveal-delay')) || 0, scrollTrigger: { trigger: el } });
      tl.fromTo(el, { opacity: 0, scale: 0.88 }, { opacity: 1, scale: 1, duration: 1.4, ease: 'expo.out' });
      var icon = el.querySelector('[data-pop-icon]');
      if (icon) tl.fromTo(icon, { scale: 0.4, rotate: -35 }, { scale: 1, rotate: 0, duration: 1.2, ease: 'back.out(1.8)' }, 0.15);
    });
    // Product rails: cards slide in from the right with a stagger while each photo settles from a slight zoom.
    $$(root, '[data-rail]').forEach(function (rail) {
      var slides = $$(rail, '.swiper-slide').filter(function (s) { return s.querySelector('[data-product-card]'); });
      if (!slides.length) return;
      var frames = slides.map(function (s) { return s.querySelector('.product-card__frame'); }).filter(Boolean);
      var shift = (parseFloat(getComputedStyle(html).fontSize) || 12) * 6;
      var tl = gsap.timeline({ scrollTrigger: { trigger: rail, start: Theme.isMobile() ? 'top bottom-=5%' : 'top bottom-=15%', once: true } });
      // Opacity stays inline (the stylesheet pre-hides slides); only the transform is handed back to Swiper.
      tl.fromTo(slides, { x: shift, rotate: 1.5, opacity: 0 }, { x: 0, rotate: 0, opacity: 1, duration: 1.4, ease: 'expo.out', stagger: 0.09, clearProps: 'transform' });
      if (frames.length) tl.fromTo(frames, { scale: 1.12 }, { scale: 1, duration: 1.8, ease: 'expo.out', stagger: 0.09, clearProps: 'transform' }, 0);
    });
    // Load choreography: marked blocks of a column rise in one after another once the page is revealed.
    $$(root, '[data-load-stagger]').forEach(function (group) {
      var items = $$(group, '[data-load-item]');
      if (!items.length) return;
      afterReady(gsap.timeline({ paused: true, delay: 0.35 }).fromTo(items, { y: 34, opacity: 0 }, { y: 0, opacity: 1, duration: 1.3, ease: 'expo.out', stagger: 0.08 }));
    });
    $$(root, '[data-fade-bottom-load]').forEach(function (el) {
      afterReady(gsap.timeline({ paused: true, delay: 0.4 }).fromTo(el, { y: 40, opacity: 0 }, { y: 0, opacity: 1 }));
    });
    // Collection CTA card: rises from below (yPercent 120 → 0, 1.4s power4.out, +.4s).
    $$(root, '[data-rise-load]').forEach(function (el) {
      afterReady(gsap.timeline({ paused: true, delay: 0.4 }).fromTo(el, { yPercent: 120, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 1.4, ease: 'power4.out' }));
    });
    // Rotating ornament (PDP summary star): 180° over 1.8s expo.inOut when its section enters.
    $$(root, '[data-rotate-in]').forEach(function (el) {
      gsap.from(el, { rotate: 180, ease: 'expo.inOut', duration: 1.8, scrollTrigger: { trigger: el.closest('section') || el, start: 'top bottom' } });
    });
    // Hero pill CTA: scale in, then the label drops into place (1.2s power4.out, +.2s).
    $$(root, '[data-intro-cta]').forEach(function (el) {
      var label = el.querySelector('[data-intro-cta-text]');
      var tl = gsap.timeline({ paused: true, delay: 0.2, defaults: { duration: 1.2, ease: 'power4.out' } })
        .fromTo(el, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1 });
      if (label) tl.from(label, { yPercent: -105 }, '<=25%');
      afterReady(tl);
    });
    $$(root, '[data-scale-load]').forEach(function (el) {
      afterReady(gsap.timeline({ paused: true, delay: 0.4, onComplete: function () { el.classList.add('hover-arrow'); } })
        .fromTo(el, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, ease: 'expo.out' }));
    });
  }

  function parallax(root) {
    $$(root, '[data-parallax]').forEach(function (el) {
      var img = el.querySelector('[data-parallax-image]');
      if (!img) return;
      if (el.hasAttribute('data-parallax-nomobile') && Theme.isMobile()) return;
      // Every scrolled-to image panel opens from an inset, rounded frame as it arrives, so large images never
      // just appear. Panels pinned to the top of the page (data-parallax-top) are already on screen and skip it.
      if (!el.hasAttribute('data-parallax-top') && !el.hasAttribute('data-no-clip')) {
        var inset = Theme.isMobile() ? 'inset(7% 6% 7% 6% round 1.4rem)' : 'inset(9% 7% 9% 7% round 2rem)';
        gsap.fromTo(el, { clipPath: inset }, {
          clipPath: 'inset(0% 0% 0% 0% round 0rem)', ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: Theme.isMobile() ? 'top 25%' : 'top 35%', scrub: Theme.isMobile() ? 0.6 : true }
        });
      }
      var top = el.hasAttribute('data-parallax-top');
      // Phones scroll natively, a frame ahead of script, so a 1:1 scrub shakes the film; a short catch-up glides.
      gsap.fromTo(img, { yPercent: top ? 0 : -20 }, {
        yPercent: 30, ease: 'none', force3D: true,
        scrollTrigger: {
          trigger: el,
          start: top ? 'top top' : 'top bottom',
          end: top ? 'bottom top' : '200% center',
          scrub: Theme.isMobile() ? 0.6 : true
        }
      });
    });
  }

  function drift(root) {
    // Drift runs on phones too (the desktop depth is the point), at a gentler amplitude for the shorter viewport.
    var factor = Theme.isMobile() ? 0.6 : 1;
    $$(root, '[data-drift]').forEach(function (el) {
      if (Theme.isMobile() && el.hasAttribute('data-drift-nomobile')) return;
      var amount = (parseInt(el.getAttribute('data-drift'), 10) || 1) * 22 * factor;
      var target = el.querySelector('[data-drift-item]') || el;
      gsap.fromTo(target, { y: amount + '%' }, {
        y: -amount + '%', ease: 'none',
        // Phones: ease after the scroll instead of tracking it frame by frame, which read as jitter on floating cards.
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: Theme.isMobile() ? 0.6 : true }
      });
    });
  }

  /* Image reveal: the frame opens from a rounded inset while the photo settles from a zoom — used on
     editorial images that aren't full-bleed parallax panels. data-reveal-img="left|right|up" sets the side
     the frame opens from (default: all sides). */
  function revealImages(root) {
    $$(root, '[data-reveal-img]').forEach(function (el) {
      var side = el.getAttribute('data-reveal-img');
      var r = getComputedStyle(el).borderTopLeftRadius || '0px';
      var from = side === 'left' ? 'inset(0% 100% 0% 0% round ' + r + ')'
        : side === 'right' ? 'inset(0% 0% 0% 100% round ' + r + ')'
        : side === 'up' ? 'inset(100% 0% 0% 0% round ' + r + ')'
        : 'inset(14% 12% 14% 12% round ' + r + ')';
      var inner = el.querySelector('img, video');
      var tl = gsap.timeline({ delay: parseFloat(el.getAttribute('data-reveal-delay')) || 0, scrollTrigger: { trigger: el, start: Theme.isMobile() ? 'top bottom-=8%' : 'top bottom-=15%' } });
      tl.fromTo(el, { clipPath: from }, { clipPath: 'inset(0% 0% 0% 0% round ' + r + ')', duration: 1.6, ease: 'expo.out' });
      if (inner) tl.fromTo(inner, { scale: 1.3 }, { scale: 1, duration: 2, ease: 'expo.out', clearProps: 'transform' }, 0);
    });
  }

  /* Scrubbed words: a paragraph's words brighten from faint to full as it scrolls through the screen. */
  function scrubWords(root) {
    $$(root, '[data-scrub-words]').forEach(function (el) {
      if (el._scrub) el._scrub.revert();
      el._scrub = SplitText.create(el, { type: 'words', wordsClass: 'scrub-word', aria: 'none' });
      gsap.fromTo(el._scrub.words, { opacity: 0.14 }, {
        opacity: 1, ease: 'none', stagger: 0.1,
        scrollTrigger: { trigger: el, start: Theme.isMobile() ? 'top 85%' : 'top 78%', end: Theme.isMobile() ? 'bottom 55%' : 'bottom 45%', scrub: 0.6 }
      });
    });
  }

  /* A rule that draws across (scaleX 0 → 1) in step with the scroll. */
  function lineDraw(root) {
    $$(root, '[data-line-draw]').forEach(function (el) {
      // Horizontal on desktop; phones stack the steps, so the rule runs down instead.
      var axis = Theme.isMobile() ? 'scaleY' : 'scaleX';
      var from = {}, to = { ease: 'none', scrollTrigger: { trigger: el.parentElement || el, start: 'top 75%', end: 'bottom 60%', scrub: 0.6 } };
      from[axis] = 0; to[axis] = 1;
      gsap.fromTo(el, from, to);
    });
  }

  /* Stagger groups that don't mark their own items: every direct child rises in turn. */
  function staggerChildren(root) {
    $$(root, '[data-stagger-children]').forEach(function (group) {
      var items = Array.prototype.slice.call(group.children);
      if (!items.length) return;
      gsap.fromTo(items, { y: 36, opacity: 0 }, { y: 0, opacity: 1, duration: 1.2, ease: 'expo.out', stagger: 0.08, scrollTrigger: { trigger: group } });
    });
  }

  /* Pointer depth (desktop): [data-depth="n"] children of a [data-depth-scene] lean toward the cursor,
     deeper layers further, on a slow expo follow. Transforms go on a wrapper-free xPercent/yPercent pair
     so they compose with drift (y) and CSS float (translate). */
  function depth(root) {
    if (!canHover) return;
    $$(root, '[data-depth-scene]').forEach(function (scene) {
      if (scene._depth) return;
      scene._depth = true;
      var layers = $$(scene, '[data-depth]').map(function (el) {
        var k = parseFloat(el.getAttribute('data-depth')) || 1;
        return { k: k, x: gsap.quickTo(el, 'xPercent', { duration: 1.6, ease: 'expo.out' }), y: gsap.quickTo(el, 'yPercent', { duration: 1.6, ease: 'expo.out' }) };
      });
      scene.addEventListener('mousemove', function (e) {
        var r = scene.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        layers.forEach(function (l) { l.x(px * 4 * l.k); l.y(py * 4 * l.k); });
      });
      scene.addEventListener('mouseleave', function () { layers.forEach(function (l) { l.x(0); l.y(0); }); });
    });
  }

  function underline(root) {
    $$(root, '.text-underline-border, .text-underline-border-2').forEach(function (el) {
      ScrollTrigger.create({
        trigger: el, start: 'top bottom-=10%', once: true,
        onEnter: function () { gsap.delayedCall(el.hasAttribute('data-underline-now') ? 0 : 1, function () { el.classList.add('active'); }); }
      });
    });
  }

  function draw(root) {
    $$(root, '[data-draw]').forEach(function (svg) {
      var paths = $$(svg, 'path, line, polyline');
      var dur = parseFloat(svg.getAttribute('data-duration')) || 1.2;
      if (svg.hasAttribute('data-draw-load')) {
        afterReady(gsap.timeline({ paused: true, delay: 0.7 }).from(paths, { drawSVG: 0, duration: dur, ease: 'circ.out', stagger: 0.1 }));
      } else {
        gsap.from(paths, { drawSVG: 0, duration: dur, ease: 'circ.out', stagger: 0.1, scrollTrigger: { trigger: svg } });
      }
    });
  }

  /* "Be … / Be …": the capsules grow out of circles one after another as the section arrives, the photo drifts
     inside them with the scroll, and the two lines slide apart. */
  function beStage(root) {
    $$(root, '[data-be]').forEach(function (sec) {
      var caps = $$(sec, '[data-be-cap]');
      if (caps.length) {
        gsap.from(caps, {
          attr: { width: 300, x: -150 }, duration: 1.8, ease: 'expo.out', stagger: 0.14,
          scrollTrigger: { trigger: sec, start: Theme.isMobile() ? 'top 80%' : 'top 65%' }
        });
      }
      var img = sec.querySelector('[data-be-img]');
      if (img) gsap.fromTo(img, { attr: { y: -170 } }, { attr: { y: -10 }, ease: 'none', scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom top', scrub: true } });
      $$(sec, '[data-be-line]').forEach(function (line) {
        // Phones: the lines nearly span the screen, so they only sway a little and never run off an edge.
        var dir = parseFloat(line.getAttribute('data-be-line')) || 1;
        var k = Theme.isMobile() ? 0.35 : 1;
        gsap.fromTo(line, { xPercent: -dir * 6 * k }, { xPercent: dir * 4 * k, ease: 'none', scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom top', scrub: 0.6 } });
      });
    });
  }

  /* Reveals whose trigger line sits below the last reachable scroll position (e.g. the footer's links, now the
     footer is short) would never fire. GSAP 3.11 has no clamp(), so once the page is scrolled to the bottom, any
     play-once reveal still waiting on an unreachable start plays. */
  function bottomGuard() {
    var late = [];
    var check = function () {
      if (!late.length) return;
      var y = Theme.lenis ? Theme.lenis.animatedScroll : window.scrollY;
      if (y < ScrollTrigger.maxScroll(window) - 4) return;
      late.forEach(function (st) { if (st.animation && st.animation.progress() < 1) st.animation.play(); });
      late = [];
    };
    ScrollTrigger.addEventListener('refresh', function () {
      var max = ScrollTrigger.maxScroll(window);
      late = ScrollTrigger.getAll().filter(function (st) { return st.animation && !st.vars.scrub && st.start > max - 2 && st.animation.progress() < 1; });
      check();
    });
    if (Theme.lenis) Theme.lenis.on('scroll', check);
    else window.addEventListener('scroll', check, { passive: true });
  }

  /* USP columns loop in CSS; scrolling the page speeds them up for a moment, in the scroll's direction. */
  function uspsBoost() {
    var tracks = $$(d, '[data-usps-track]');
    if (!tracks.length || !Theme.lenis || !tracks[0].getAnimations) return;
    var rate = 1, target = 1, applied = 1;
    Theme.lenis.on('scroll', function (l) { target = 1 + Math.min(Math.abs(l.velocity) / 6, 5); });
    gsap.ticker.add(function () {
      target += (1 - target) * 0.06;
      rate += (target - rate) * 0.12;
      if (Math.abs(rate - 1) < 0.002) rate = 1;
      if (rate === applied) return;
      applied = rate;
      tracks.forEach(function (t) { t.getAnimations().forEach(function (a) { if (a.playState === 'running') a.playbackRate = rate; }); });
    });
  }

  function magnet(root) {
    if (!canHover) return;
    $$(root, '.magnet').forEach(function (el) {
      if (el._magnet) return;
      el._magnet = true;
      var qx = gsap.quickTo(el, 'x', { duration: 1, ease: 'expo.out' });
      var qy = gsap.quickTo(el, 'y', { duration: 1, ease: 'expo.out' });
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        qx(((e.clientX - r.left) / el.offsetWidth - 0.5) * 35);
        qy(((e.clientY - r.top) / el.offsetHeight - 0.5) * 35);
      });
      el.addEventListener('mouseleave', function () { qx(0); qy(0); });
    });
  }

  function iconClone(root) {
    $$(root, '.icon-clone').forEach(function (el) {
      if (el.hasAttribute('data-clone')) return;
      var svg = el.querySelector('[data-clone-target] svg, svg');
      if (!svg) return;
      el.setAttribute('data-clone', '');
      var copy = svg.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      svg.parentNode.appendChild(copy);
      // A [data-clone-host] ancestor (e.g. a product card) can drive the hover for the whole tile.
      var host = el.closest('[data-clone-host]') || el;
      if (canHover) {
        // A short grace period on leave: magnets and hover growth can nudge an element out from under the pointer
        // for a frame, and toggling on every flicker froze the swap half-way.
        host.addEventListener('mouseenter', function () { clearTimeout(el._leaveT); el.classList.add('is-hover'); });
        host.addEventListener('mouseleave', function () {
          clearTimeout(el._leaveT);
          el._leaveT = setTimeout(function () { el.classList.remove('is-hover'); }, 90);
        });
        return;
      }
      // Touch: a tap plays the same swap (cards get theirs from the in-view state below).
      if (host !== el) return;
      el.addEventListener('pointerdown', function () {
        el.classList.add('is-hover');
        clearTimeout(el._cloneT);
        el._cloneT = setTimeout(function () { el.classList.remove('is-hover'); }, 900);
      }, { passive: true });
    });
  }
  // Late-inserted cards (filters, load more, search, recommendations) come through here, so wire their touch states too.
  Theme.iconClone = function (root) { iconClone(root); touchStates(root); cardTilt(root); };

  /* Card tilt (desktop): the card leans toward the pointer (CSS reads --rx / --ry). */
  function cardTilt(root) {
    if (!canHover || !motionOn) return;
    $$(root, '[data-product-card]').forEach(function (card) {
      if (card._tilt) return;
      card._tilt = true;
      var raf = 0, ex = 0, ey = 0;
      card.addEventListener('pointermove', function (e) {
        ex = e.clientX; ey = e.clientY;
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          var r = card.getBoundingClientRect();
          var x = (ex - r.left) / r.width - 0.5, y = (ey - r.top) / r.height - 0.5;
          card.style.setProperty('--rx', (-y * 7).toFixed(2) + 'deg');
          card.style.setProperty('--ry', (x * 8).toFixed(2) + 'deg');
        });
      });
      card.addEventListener('pointerleave', function () {
        card.style.setProperty('--rx', '0deg');
        card.style.setProperty('--ry', '0deg');
      });
    });
  }

  /* Hamburger tap on touch screens replays its hover stagger (icons get their swap from iconClone). */
  d.addEventListener('pointerdown', function (e) {
    if (canHover) return;
    var el = e.target.closest('.navbar__hamburger');
    if (!el) return;
    el.classList.remove('is-play');
    void el.offsetWidth;
    el.classList.add('is-play');
    clearTimeout(el._playT);
    el._playT = setTimeout(function () { el.classList.remove('is-play'); }, 900);
  }, { passive: true });

  /* Touch stand-ins for the desktop hover layer:
     - product cards take their hover state (alt photo, zoom, filled quick-add, underline) while they sit
       in the middle band of the screen, so scrolling a rail or grid animates like moving a cursor over it;
     - magnetic buttons give a springy press. */
  /* Product films on cards: load and play while the card is hovered (touch: while it's the active card), fade in
     once frames are actually playing, pause and rewind when it's left. Delegated, so late-inserted cards work. */
  Theme.cardFilm = function (card, on) {
    var v = card && card.querySelector('[data-card-film]');
    if (!v) return;
    if (on) {
      if (!v._filmBound) {
        v._filmBound = true;
        v.addEventListener('playing', function () { if (v._want) card.classList.add('is-film'); });
      }
      v._want = true;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    } else {
      v._want = false;
      card.classList.remove('is-film');
      v.pause();
      setTimeout(function () { if (!v._want) try { v.currentTime = 0; } catch (e) {} }, 700);
    }
  };
  if (canHover) {
    d.addEventListener('mouseover', function (e) {
      var card = e.target.closest && e.target.closest('.product-card.has-film');
      if (card && !card.contains(e.relatedTarget)) Theme.cardFilm(card, true);
    });
    d.addEventListener('mouseout', function (e) {
      var card = e.target.closest && e.target.closest('.product-card.has-film');
      if (card && !card.contains(e.relatedTarget)) Theme.cardFilm(card, false);
    });
  }

  var cardObserver = !canHover && 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      var card = en.target;
      var on = en.isIntersecting && en.intersectionRatio >= 0.35;
      clearTimeout(card._activeT);
      if (on === card.classList.contains('is-active')) return;
      // Settle before switching on so a fast fling doesn't strobe every card it passes.
      card._activeT = setTimeout(function () {
        card.classList.toggle('is-active', on);
        var q = card.querySelector('.icon-clone');
        if (q) q.classList.toggle('is-hover', on);
        Theme.cardFilm(card, on);
      }, on ? 450 : 0);
    });
  }, { rootMargin: '-22% 0px -22% 0px', threshold: [0, 0.35, 0.6, 1] }) : null;

  function touchStates(root) {
    if (canHover || !motionOn) return;
    if (cardObserver) {
      $$(root, '[data-product-card]').forEach(function (card) {
        if (card._touchObserved) return;
        card._touchObserved = true;
        cardObserver.observe(card);
      });
    }
    $$(root, '.magnet').forEach(function (el) {
      if (el._press) return;
      el._press = true;
      el.addEventListener('pointerdown', function () { gsap.to(el, { scale: 0.93, duration: 0.25, ease: 'power3.out', overwrite: 'auto' }); }, { passive: true });
      var release = function () { gsap.to(el, { scale: 1, duration: 1, ease: 'elastic.out(1, 0.4)', overwrite: 'auto' }); };
      el.addEventListener('pointerup', release);
      el.addEventListener('pointercancel', release);
      el.addEventListener('pointerleave', release);
    });
  }

  // Background videos: start after the preloader/transition, and only while on screen.
  var videoObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      var v = en.target;
      if (en.isIntersecting && v.offsetParent !== null) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
      else v.pause();
    });
  }, { rootMargin: '100px 0px' }) : null;
  function videos(root) {
    if (!motionOn || !videoObserver) return;
    $$(root, 'video[data-play-after-ready]').forEach(function (v) {
      if (v._observed) return;
      v._observed = true;
      Theme.ready.then(function () { videoObserver.observe(v); });
    });
  }

  Theme.initMotion = function (root) {
    root = root || d;
    magnet(root);
    iconClone(root);
    touchStates(root);
    cardTilt(root);
    videos(root);
    if (!motionOn) {
      $$(root, '.text-underline-border, .text-underline-border-2').forEach(function (el) { el.classList.add('active'); });
      return;
    }
    var key = root === d ? html : root;
    var prev = ctxs.get(key);
    if (prev) prev.revert();
    var ctx = gsap.context(function () {
      splitReveal(root);
      fades(root);
      parallax(root);
      drift(root);
      revealImages(root);
      staggerChildren(root);
      scrubWords(root);
      lineDraw(root);
      underline(root);
      draw(root);
      beStage(root);
    });
    depth(root);
    ctxs.set(key, ctx);
    return ctx;
  };
  Theme.revertMotion = function (root) {
    var ctx = ctxs.get(root);
    if (ctx) { ctx.revert(); ctxs.delete(root); }
    $$(root, '[data-split]').forEach(function (el) { el._split = null; });
  };

  /* ==========================================================================
     Header: transparent/white over hero, solid after (innerHeight - 300),
     hides on scroll down after 500px, returns on scroll up.
     ========================================================================== */
  function initHeader() {
    var header = d.querySelector('[data-header]');
    if (!header) return;
    var transparent = header.hasAttribute('data-transparent');
    var ann = d.querySelector('[data-announcement]');
    var lastY = window.scrollY;
    var update = function (y, dir) {
      var annH = ann && !ann.classList.contains('is-hidden') ? ann.offsetHeight : 0;
      header.style.setProperty('--ann-offset', Math.max(0, annH - y) + 'px');
      // Over the home hero the bar stays clear until the hero has nearly passed; elsewhere it firms up at once so
      // links never sit on top of page content.
      var solid = transparent ? y > window.innerHeight - 300 : y > 24;
      header.classList.toggle('is-active', solid);
      if (transparent) header.classList.toggle('is-white', !solid);
      if (locks || header.querySelector('.navbar.is-open') || header.classList.contains('is-mega')) return;
      if (y <= 500) header.classList.remove('is-hidden');
      else if (dir === 1) header.classList.add('is-hidden');
      else if (dir === -1) header.classList.remove('is-hidden');
    };
    if (Theme.lenis) {
      Theme.lenis.on('scroll', function (l) { update(l.animatedScroll, l.direction); });
    } else {
      window.addEventListener('scroll', function () {
        var y = window.scrollY;
        update(y, y > lastY ? 1 : y < lastY ? -1 : 0);
        lastY = y;
      }, { passive: true });
    }
    update(window.scrollY, 0);
    header.addEventListener('focusin', function () { header.classList.remove('is-hidden'); });

    initMega(header);

    // Mobile menu
    var navbar = header.querySelector('.navbar');
    var toggle = header.querySelector('[data-menu-toggle]');
    var menu = header.querySelector('[data-menu]');
    if (!toggle || !menu) return;
    var menuLinks = $$(menu, '.navbar__item > a, .navbar__item > .navbar__mega-toggle');
    var menuExtra = $$(menu, '.navbar__menu > .menu-extra');
    var setOpen = function (open) {
      navbar.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open);
      toggle.setAttribute('aria-label', open ? toggle.dataset.labelClose : toggle.dataset.labelOpen);
      // Links rise out of their masks one after another as the panel slides in (same line reveal as the headings).
      if (open && motionOn && Theme.isMobile()) {
        gsap.fromTo(menuLinks, { yPercent: 110 }, { yPercent: 0, duration: 1.1, ease: 'expo.out', stagger: 0.06, delay: 0.4, overwrite: true });
        gsap.fromTo(menuExtra, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 1.1, ease: 'expo.out', stagger: 0.1, delay: 0.6, overwrite: true });
      }
      // Phones: the menu opens with the Shop categories already showing (the same ones as the desktop sheet).
      if (open && Theme.isMobile() && Theme.openMega) Theme.openMega();
      if (open) { Theme.lockScroll(); Theme.trapFocus(navbar, menu.querySelector('a, button')); }
      else { Theme.unlockScroll(); Theme.releaseFocus(); if (Theme.closeMega) Theme.closeMega(); }
    };
    toggle.addEventListener('click', function () { setOpen(!navbar.classList.contains('is-open')); });
    menu.addEventListener('click', function (e) { if (e.target.closest('a')) setOpen(false); });
    d.addEventListener('keydown', function (e) { if (e.key === 'Escape' && navbar.classList.contains('is-open')) setOpen(false); });
    mqMobile.addEventListener('change', function () { if (navbar.classList.contains('is-open')) setOpen(false); });
  }

  /* Shop megamenu — opens and closes on a click of Shop only (desktop and the phone menu alike; client,
     2026-09-29). Escape, the dimmed backdrop, a click on a card or scrolling the page away closes it on desktop. */
  function initMega(header) {
    var item = header.querySelector('[data-mega]');
    var toggle = item && item.querySelector('[data-mega-toggle]');
    var panel = item && item.querySelector('[data-mega-panel]');
    if (!toggle || !panel) return;
    var backdrop = header.querySelector('[data-mega-backdrop]');

    var isOpen = function () { return panel.classList.contains('is-open'); };
    var set = function (open) {
      if (open === isOpen()) return;
      panel.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open);
      if (Theme.isMobile()) return;
      header.classList.toggle('is-mega', open);
      if (open) header.classList.remove('is-hidden');
    };
    Theme.closeMega = function () { set(false); };
    Theme.openMega = function () { set(true); };
    toggle.addEventListener('click', function () { set(!isOpen()); });
    if (backdrop) backdrop.addEventListener('click', function () { set(false); });
    d.addEventListener('click', function (e) { if (isOpen() && !Theme.isMobile() && !item.contains(e.target)) set(false); });
    d.addEventListener('keydown', function (e) { if (e.key === 'Escape' && isOpen()) { set(false); toggle.focus(); } });
    // Scrolling the page away closes the sheet on desktop.
    var y0 = 0;
    var onScroll = function (y) { if (!isOpen() || Theme.isMobile()) { y0 = y; return; } if (Math.abs(y - y0) > 90) set(false); };
    if (Theme.lenis) Theme.lenis.on('scroll', function (l) { onScroll(l.animatedScroll); });
    else window.addEventListener('scroll', function () { onScroll(window.scrollY); }, { passive: true });
    panel.addEventListener('click', function (e) { if (e.target.closest('a')) set(false); });
  }
  /* Delegated clicks (theme-editor reloads keep working): footer link columns open on a click of their title,
     and [data-scroll-next] arrows scroll to the following section. */
  function initFootDrops() {
    d.addEventListener('click', function (e) {
      // Round "scroll down" arrows jump to the next section.
      var next = e.target.closest('[data-scroll-next]');
      if (next) {
        var sec = next.closest('.shopify-section');
        var to = sec && sec.nextElementSibling;
        if (to) { if (Theme.scrollTo) Theme.scrollTo(to, { duration: 1.4 }); else to.scrollIntoView({ behavior: 'smooth' }); }
        return;
      }
      var btn = e.target.closest('[data-foot-toggle]');
      if (!btn) return;
      var drop = btn.closest('[data-foot-drop]');
      var open = !drop.classList.contains('is-open');
      drop.classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', open);
      if (motionOn) setTimeout(function () { ScrollTrigger.refresh(); }, 750);
    });
  }

  /* The WhatsApp button steps up while the home hero's wide button is on screen, so the two never overlap on phones. */
  function initWaWidget() {
    var widget = d.querySelector('.wa-widget');
    var cta = d.querySelector('.hero__cta');
    if (!widget || !cta || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      widget.classList.toggle('is-lifted', entries[0].isIntersecting);
    }).observe(cta);
  }

  function initAnnouncement() {
    var ann = d.querySelector('[data-announcement]');
    if (!ann) return;
    var key = 'mirah:ann:' + (ann.getAttribute('data-announcement') || '');
    var close = ann.querySelector('[data-announcement-close]');
    // A bar that can't be closed always shows, whatever an earlier visit stored.
    try { if (close && sessionStorage.getItem(key)) ann.classList.add('is-hidden'); } catch (e) {}

    // Several messages: they rotate by themselves (pausing under the pointer or focus) and the arrows step by hand.
    var slides = $$(ann, '[data-announcement-slide]');
    if (slides.length > 1) {
      var wrap = ann.querySelector('[data-announcement-slides]');
      var interval = (parseFloat(wrap.getAttribute('data-interval')) || 5) * 1000;
      var index = 0, timer = null;
      var go = function (n) {
        var from = slides[index];
        index = (n + slides.length) % slides.length;
        from.classList.remove('is-active');
        from.classList.add('is-leaving');
        from.setAttribute('aria-hidden', 'true');
        setTimeout(function () { from.classList.remove('is-leaving'); }, 650);
        slides[index].classList.remove('is-leaving');
        slides[index].classList.add('is-active');
        slides[index].removeAttribute('aria-hidden');
      };
      var stop = function () { clearInterval(timer); timer = null; };
      var play = function () { stop(); timer = setInterval(function () { go(index + 1); }, interval); };
      var prev = ann.querySelector('[data-announcement-prev]'), next = ann.querySelector('[data-announcement-next]');
      if (prev) prev.addEventListener('click', function () { go(index - 1); play(); });
      if (next) next.addEventListener('click', function () { go(index + 1); play(); });
      ann.addEventListener('mouseenter', stop);
      ann.addEventListener('mouseleave', play);
      ann.addEventListener('focusin', stop);
      ann.addEventListener('focusout', play);
      d.addEventListener('visibilitychange', function () { if (d.hidden) stop(); else play(); });
      play();
    }
    if (close) close.addEventListener('click', function () {
      ann.classList.add('is-hidden');
      try { sessionStorage.setItem(key, '1'); } catch (e) {}
      var header = d.querySelector('[data-header]');
      if (header) header.style.setProperty('--ann-offset', '0px');
    });
  }

  /* ==========================================================================
     Accordions — [data-accordion] > [data-accordion-toggle] + [data-accordion-content]
     ========================================================================== */
  Theme.initAccordions = function (root) {
    $$(root || d, '[data-accordion]').forEach(function (acc) {
      if (acc._acc) return;
      acc._acc = true;
      var btn = acc.querySelector('[data-accordion-toggle]');
      var content = acc.querySelector('[data-accordion-content]');
      if (!btn || !content) return;
      var set = function (open, instant) {
        acc.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open);
        if (instant) content.style.transition = 'none';
        content.style.maxHeight = open ? content.scrollHeight + 'px' : '0px';
        if (instant) { content.offsetHeight; content.style.removeProperty('transition'); }
      };
      set(acc.hasAttribute('data-open'), true);
      btn.addEventListener('click', function () {
        var open = !acc.classList.contains('is-open');
        var group = acc.getAttribute('data-accordion-group');
        if (open && group) {
          $$(d, '[data-accordion-group="' + group + '"].is-open').forEach(function (o) {
            if (o !== acc) { o.classList.remove('is-open'); o.querySelector('[data-accordion-content]').style.maxHeight = '0px'; o.querySelector('[data-accordion-toggle]').setAttribute('aria-expanded', false); }
          });
        }
        set(open);
      });
      content.addEventListener('transitionend', function () {
        if (acc.classList.contains('is-open')) content.style.maxHeight = content.scrollHeight + 'px';
        if (window.ScrollTrigger) ScrollTrigger.refresh();
      });
    });
  };

  /* ==========================================================================
     Preloader — reference choreography: rolling digits, split wordmark,
     centre line, halves collapse. First visit per session only.
     ========================================================================== */
  function runPreloader() {
    var pl = d.querySelector('[data-preloader]');
    if (!pl) return Promise.resolve();
    if (!motionOn || design || html.classList.contains('pl-skip')) { pl.remove(); return Promise.resolve(); }
    try { sessionStorage.setItem('mirah:pl', '1'); } catch (e) {}

    var waits = $$(d, 'main img').filter(function (i) { return i.loading !== 'lazy'; }).slice(0, 6).map(function (img) {
      return img.complete ? Promise.resolve() : new Promise(function (r) { img.addEventListener('load', r, { once: true }); img.addEventListener('error', r, { once: true }); });
    });
    if (d.fonts && d.fonts.ready) waits.push(d.fonts.ready);
    var loaded = Promise.race([Promise.all(waits), new Promise(function (r) { setTimeout(r, 3500); })]);

    return loaded.then(function () {
      return new Promise(function (resolve) {
        var left = pl.querySelector('[data-digits-left]');
        var right = pl.querySelector('[data-digits-right]');
        var nL = left.children.length, nR = right.children.length;
        var logo = $$(pl, '[data-logo-half]');
        var bg = $$(pl, '[data-bg-half]');
        var line = pl.querySelector('[data-line]');
        var spinner = pl.querySelector('[data-spinner]');
        var tl = gsap.timeline({
          defaults: { ease: 'expo.inOut', duration: 1 },
          onComplete: function () { pl.remove(); resolve(); }
        });
        var step = { ease: 'expo.out', duration: 0.6 };
        for (var i = 0; i < nR; i++) {
          if (i < nR - 2) tl.to(left, Object.assign({ yPercent: '-=' + 100 / nL }, step));
          if (i < nR - 1) tl.to(right, Object.assign({ yPercent: '-=' + 100 / nR }, step), i < nR - 2 ? '<=10%' : undefined);
        }
        tl.to(left, Object.assign({ yPercent: '-=' + 100 / nL }, step));
        tl.to(right, Object.assign({ yPercent: '-=' + 100 / nR }, step), '<=10%');
        tl.to(logo, { y: 0, stagger: 0.1, duration: 1.4, onStart: function () { gsap.to(spinner, { opacity: 0 }); } }, '<-=20%');
        tl.to(line, { scaleY: 1 }, '<=95%');
        tl.to(line, { opacity: 0, ease: 'power1.out' }, '<=50%');
        tl.to(logo, { y: gsap.utils.wrap(['105%', '-105%']), stagger: 0.1 }, '<=10%');
        tl.to(bg, { scaleY: 0, stagger: 0.1 }, '<=30%');
      });
    });
  }
  /* ==========================================================================
     Page transitions — off at the client's request (2026-09-29): links navigate straight away, with no curtain
     and no card → PDP morph. Only a stale bfcache flag is cleared.
     ========================================================================== */
  function initTransitions() {
    html.classList.remove('is-entering', 'is-leaving', 'vt-from-card');
    try { sessionStorage.removeItem('mirah:transition'); } catch (e) {}
    return Promise.resolve();
  }
  /* ==========================================================================
     Custom scrollbar (desktop, Lenis only)
     ========================================================================== */
  function initScrollbar() {
    var bar = d.querySelector('[data-scrollbar]');
    if (!bar) return;
    if (!Theme.lenis || !settings.scrollbar || !canHover || Theme.isMobile()) { bar.remove(); return; }
    var thumb = bar.querySelector('[data-scrollbar-thumb]');
    var inner = bar.querySelector('[data-scrollbar-inner]');
    var dragging = false;
    var sync = function () {
      bar.classList.toggle('is-hidden', d.documentElement.scrollHeight <= window.innerHeight + 2);
    };
    Theme.lenis.on('scroll', function (l) {
      gsap.set(thumb, { y: l.progress * (window.innerHeight - thumb.offsetHeight - 48), force3D: true });
    });
    thumb.addEventListener('pointerdown', function (e) { dragging = true; bar.classList.add('is-dragging'); e.preventDefault(); });
    window.addEventListener('pointerup', function () { dragging = false; bar.classList.remove('is-dragging'); });
    window.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      e.preventDefault();
      var h = inner.getBoundingClientRect().height;
      var m = (window.innerHeight - h) / 2;
      var p = gsap.utils.clamp(0, 1, gsap.utils.mapRange(0, window.innerHeight, -m, h + m)(e.clientY) / h);
      Theme.lenis.scrollTo(Theme.lenis.limit * p, { duration: 1, force: true });
    }, { passive: false });
    sync();
    new ResizeObserver(Theme.debounce(sync, 150)).observe(d.body);
  }

  /* ==========================================================================
     Hover cursor (desktop): a frosted disc trails the pointer on a slow expo
     follow and grows in with a label over anything marked [data-cursor]
     (cards → "View", PDP gallery → "Zoom"). It steps aside over buttons.
     ========================================================================== */
  function initCursor() {
    if (!canHover || !motionOn || design) return;
    var el = d.createElement('div');
    el.className = 'cursor';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<span class="cursor__label"></span>';
    d.body.appendChild(el);
    var label = el.firstChild;
    gsap.set(el, { xPercent: -50, yPercent: -50, scale: 0 });
    var qx = gsap.quickTo(el, 'x', { duration: 0.8, ease: 'expo.out' });
    var qy = gsap.quickTo(el, 'y', { duration: 0.8, ease: 'expo.out' });
    var shown = false, text = '';
    var show = function (on) {
      if (on === shown) return;
      shown = on;
      gsap.to(el, { scale: on ? 1 : 0, duration: on ? 0.7 : 0.45, ease: 'expo.out', overwrite: 'auto' });
    };
    window.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      qx(e.clientX); qy(e.clientY);
      var t = e.target.closest ? e.target.closest('[data-cursor]') : null;
      var onControl = t && e.target.closest('button:not([data-zoom]), input, select, label, .product-card__actions, .pdp__nav');
      if (t && !onControl) {
        var next = t.getAttribute('data-cursor');
        if (next !== text) { text = next; label.textContent = next; }
        show(true);
      } else show(false);
    }, { passive: true });
    d.documentElement.addEventListener('mouseleave', function () { show(false); });
    // Wheel scrolling moves the page under a still pointer without any pointermove, so re-check what's beneath it.
    var lastX = -1, lastY = -1;
    window.addEventListener('pointermove', function (e) { lastX = e.clientX; lastY = e.clientY; }, { passive: true });
    var recheck = function () {
      if (lastX < 0) return;
      var el = d.elementFromPoint(lastX, lastY);
      var t = el && el.closest ? el.closest('[data-cursor]') : null;
      if (!t || el.closest('button:not([data-zoom]), input, select, label, .product-card__actions, .pdp__nav')) show(false);
      else { text = t.getAttribute('data-cursor'); label.textContent = text; show(true); }
    };
    if (Theme.lenis) Theme.lenis.on('scroll', recheck); else window.addEventListener('scroll', recheck, { passive: true });
    window.addEventListener('pagehide', function () { show(false); });
  }

  // After the loader/curtain: the header settles in from above, item by item.
  function introHeader() {
    if (!motionOn) return;
    var header = d.querySelector('[data-header]');
    if (!header) return;
    var items = Theme.isMobile()
      ? $$(header, '.navbar__hamburger, .navbar__logo, .navbar__cta')
      : $$(header, '.navbar__logo, .navbar__list > li, .navbar__cta');
    gsap.from(items, { y: -18, opacity: 0, duration: 1.1, ease: 'expo.out', stagger: 0.06, clearProps: 'transform,opacity' });
  }

  /* ==========================================================================
     Boot
     ========================================================================== */
  // Cross-document view transitions reject their promises when the next page (e.g. checkout) doesn't opt in;
  // that's expected, so keep it out of the console.
  var quietVT = function (e) {
    var vt = e.viewTransition;
    if (!vt) return;
    [vt.ready, vt.finished, vt.updateCallbackDone].forEach(function (p) { if (p && p.catch) p.catch(function () {}); });
  };
  window.addEventListener('pageswap', quietVT);
  window.addEventListener('pagereveal', quietVT);
  window.addEventListener('unhandledrejection', function (e) {
    var r = e.reason;
    if (r && r.name === 'InvalidStateError' && /transition/i.test(r.message || '')) e.preventDefault();
  });

  function boot() {
    initAnnouncement();
    initWaWidget();
    initHeader();
    initFootDrops();
    if (motionOn) { uspsBoost(); bottomGuard(); }
    Theme.initAccordions(d);
    Theme.initMotion(d);
    initScrollbar();

    var gate = html.classList.contains('pl-skip') || !d.querySelector('[data-preloader]') ? initTransitions() : Promise.all([runPreloader(), initTransitions()]);
    gate.then(function () { readyResolve(); introHeader(); });
    initCursor();

    if (motionOn) {
      var refresh = Theme.debounce(function () { ScrollTrigger.refresh(); }, 150);
      window.addEventListener('load', refresh);
      if (d.fonts && d.fonts.ready) d.fonts.ready.then(refresh);
      var lastH = d.body.offsetHeight;
      new ResizeObserver(function () {
        var h = d.body.offsetHeight;
        if (Math.abs(h - lastH) > 2) { lastH = h; refresh(); }
      }).observe(d.body);
      if (!design) {
        mqMobile.addEventListener('change', function () { window.location.reload(); });
      }
    }
    window.addEventListener('resize', Theme.debounce(setVh, 200));
  }

  /* Theme editor: re-run motion for re-rendered sections, clean up on unload */
  d.addEventListener('shopify:section:load', function (e) {
    Theme.initAccordions(e.target);
    Theme.initMotion(e.target);
    if (motionOn) ScrollTrigger.refresh();
  });
  d.addEventListener('shopify:section:unload', function (e) { Theme.revertMotion(e.target); });

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
